import { useLayoutEffect } from 'react';
import { API_URL } from '@constants';
import { useData } from '@hooks/useData';
import { GetAllSettingsSuccessResponse } from '@services/useGetAllSettings';

const loadFontIfNeeded = (fontFamily: string, options?: { weights?: number[] }) => {
    const formattedFamily = fontFamily.trim();
    const normalizedWeights = options?.weights?.length ? Array.from(new Set(options.weights)).sort((a, b) => a - b).join(',') : '';
    const query = new URLSearchParams({
        family: formattedFamily,
        italics: '1',
    });

    if (normalizedWeights) {
        query.set('weights', normalizedWeights);
    }

    const fontURL = `${API_URL}/font-stylesheet?${query.toString()}`;

    if (!document.querySelector(`link[href="${fontURL}"]`)) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = fontURL;
        document.head.appendChild(link);
    }
};

const applySettingsToCSS = (settings: GetAllSettingsSuccessResponse['data']) => {
    const root = document.documentElement;

    if (settings?.layouts) {
        const findValue = (name: string) => settings?.layouts?.find((item) => item.name === name)?.value;

        root.style.setProperty('--background-website-color', findValue('primary color') || '#000');
        root.style.setProperty('--prices-color', findValue('secondary color') || '#000');
        root.style.setProperty('--buttons-color', findValue('buttons color') || '#000');
        root.style.setProperty('--titles-color', findValue('titles color') || '#000');
        root.style.setProperty('--subtitles-color', findValue('subtitles color') || '#000');
        root.style.setProperty('--website-font-type', findValue('fontType') || 'sans-serif');

        /* cspell:disable */
        const fontFamily = (findValue('fontfamily') || 'Mulish').trim();
        /* cspell:enable */
        root.style.setProperty('--website-font-family', fontFamily);
        loadFontIfNeeded(fontFamily, { weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] });

        const secondaryFontFamily = findValue('secondaryFontFamily')?.trim();
        const secondaryFontType = findValue('secondaryFontType')?.trim();

        if (secondaryFontFamily) {
            root.style.setProperty('--website-secondary-font-family', secondaryFontFamily);
            loadFontIfNeeded(secondaryFontFamily, { weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] });
        } else {
            root.style.removeProperty('--website-secondary-font-family');
        }

        if (secondaryFontType) {
            root.style.setProperty('--website-secondary-font-type', secondaryFontType);
        } else {
            root.style.removeProperty('--website-secondary-font-type');
        }
    }

    // Add global CSS from settings, ensuring it overrides defaults
    try {
        // globalCss now comes inside settings.layouts as an item value
        const globalCss: string | undefined = settings?.layouts
            ?.find((item) => item.name === 'globalCSS')
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ?.value as any;

        const styleId = 'global-settings-css';
        const existing = document.getElementById(styleId) as HTMLStyleElement | null;
        console.log(existing)

        if (globalCss && typeof globalCss === 'string' && globalCss.trim().length > 0) {
            const cssContent = globalCss.trim();

            if (existing) {
                if (existing.textContent !== cssContent) {
                    existing.textContent = cssContent;
                }
            } else {
                const style = document.createElement('style');
                style.id = styleId;
                style.type = 'text/css';
                style.textContent = cssContent;
                // Append as late as possible so it overrides prior styles
                document.head.appendChild(style);
            }
        } else if (existing) {
            existing.parentElement?.removeChild(existing);
        }
    } catch (e) { /* empty */ }
};

export const GlobalStylesUpdater = () => {
    const { allSettings } = useData();

    useLayoutEffect(() => {
        if (allSettings?.data) {
            applySettingsToCSS(allSettings.data);
        }
    }, [allSettings]);

    return null;
};
